// Library Imports
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import Cookies from "js-cookie";

// Local Imports (font groups)
import { FONT_GROUPS } from '@/utils/fontGroups';

// Local Imports
import { setAuthToken } from '@/redux/http';
import { updateCurrentUser } from '@/redux/features/CurrentUser/currentUserSlice';
import { setConnectionStatus } from '@/redux/features/InternetConnect/InternetConnectSlice';
import { closeInstagramErrorModal, openInstagramErrorModal } from '@/redux/features/ui/uiSlice';
import { getSocialMediaPostsListAPIFn, setSocialPostsCacheEntry } from '@/redux/features/manageMediaKit/manageKitSlice';

// ==================== Authentication Hook ====================
export const useIsAuthenticatedUser = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => state?.currentUser?.currentUser);
  const token = user?.accessToken;

  // Initialize state with current token check for immediate return
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (token) {
      setAuthToken(token);
      return true;
    }
    return false;
  });

  useEffect(() => {
    // Check if token exists in Redux store
    if (token) {
      setAuthToken(token);
      setIsAuthenticated(true);
      return;
    }

    // If no token in store, check cookie
    if (typeof window !== 'undefined') {
      const wpToken = Cookies.get("_wp_knock");
      if (wpToken) {
        try {
          const loginData = JSON.parse(atob(wpToken));
          const cookieToken = loginData?.currentUser?.accessToken;

          if (cookieToken) {
            // Only restore if cookie token differs from Redux state
            if (cookieToken !== token) {
              // Restore user data from cookie to Redux store
              dispatch(updateCurrentUser({
                accessToken: loginData?.currentUser?.accessToken,
                email: loginData?.currentUser?.email,
                userRole: loginData?.currentUser?.userRole,
                username: loginData?.currentUser?.username,
                refreshToken: loginData?.currentUser?.refreshToken,
                userId: loginData?.currentUser?.userId,
                isInstagramAuthentcated: loginData?.currentUser?.isInstagramAuthentcated,
                emailConfirmed: loginData?.currentUser?.emailConfirmed,
              }));
            }

            setAuthToken(cookieToken);
            setIsAuthenticated(true);
            return;
          }
        } catch (error) {
          // Handle malformed cookies gracefully
          console.error('Error parsing cookie token:', error);
          setIsAuthenticated(false);
          return;
        }
      }
    }

    setIsAuthenticated(false);
  }, [token, dispatch]);

  return isAuthenticated;
};

// ==================== Animation Hook ====================
export function useCountAnimation(target, duration = 800) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    let start = 0;
    let startTime = null;

    function animate(time) {
      if (!startTime) startTime = time;
      const progress = Math.min((time - startTime) / duration, 1);
      const current = Math.floor(start + (target - start) * progress);
      setValue(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    }

    requestAnimationFrame(animate);
  }, [target, duration]);

  return value;
}

export const AnimatedCount = ({ value, duration }) => useCountAnimation(Number(value) || 0, duration);

// ==================== Auto Refresh Hook ====================
export function useAutoRefreshOnInactivity() {
  useEffect(() => {
    let leftAt = null;
    const THRESHOLD = 5 * 60 * 1000;

    const onBlur = () => {
      leftAt = Date.now();
    };

    const onFocus = () => {
      if (leftAt && Date.now() - leftAt >= THRESHOLD) {
        window.location.reload();
      }
    };

    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
    };
  }, []);
}

// ==================== Intro Video Hook ====================
export const useIntroMediaFile = (mediaKitDetails) => {
  const [fileType, setFileType] = useState('video');
  const [selectedFile, setSelectedFile] = useState({
    thumbNail: null,
    video: null,
    image: null,
    uploadProgress: ''
  });

  useEffect(() => {
    const hasVideo =
      !!mediaKitDetails?.isProfileVideoPriority && !!mediaKitDetails?.profileVideoThumbnailUrl;

    setFileType(hasVideo ? 'video' : 'image');
    setSelectedFile(
      hasVideo
        ? {
          thumbNail: mediaKitDetails?.profileVideoThumbnailUrl || null,
          video: mediaKitDetails?.profileVideoUrl || null,
          image: mediaKitDetails?.profilePicUrl || null,
          uploadProgress: ''
        }
        : {
          thumbNail: mediaKitDetails?.profileVideoThumbnailUrl || null,
          video: mediaKitDetails?.profileVideoUrl || null,
          image: mediaKitDetails?.profilePicUrl || null,
          uploadProgress: ''
        }
    );
  }, [
    mediaKitDetails?.isProfileVideoPriority,
    mediaKitDetails?.profileVideoThumbnailUrl,
    mediaKitDetails?.profileVideoUrl,
    mediaKitDetails?.profilePicUrl
  ]);

  return { fileType, setFileType, selectedFile, setSelectedFile };
};

// ==================== Global Instagram Error Modal Hook ====================
export const useInstagramErrorModal = () => {
  const dispatch = useDispatch();

  return {
    openInstagramErrorModal: () => dispatch(openInstagramErrorModal()),
    closeInstagramErrorModal: () => dispatch(closeInstagramErrorModal()),
  };
};

// ==================== Timer Hook ====================
export const useTimer = initialValue => {
  const [timer, setTimer] = useState(initialValue);

  useEffect(() => {
    if (timer <= 0) return;

    const timerID = setInterval(() => {
      setTimer(prevTimer => prevTimer - 1);
    }, 1000);

    return () => clearInterval(timerID);
  }, [timer]);

  return [timer, setTimer];
};

// ==================== Outside Click Hook ====================
export const useOutsideClick = (id, handler) => {
  const handleClickOutside = useCallback(
    event => {
      if (!id) return;

      const element = document.getElementById(id);
      const childElement = document.getElementById(`popover-${id}`);

      if (
        element &&
        childElement &&
        !element.contains(event.target) &&
        !childElement.contains(event.target)
      ) {
        handler();
      }
    },
    [id, handler]
  );

  useEffect(() => {
    if (!id) return;

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleClickOutside, id]);
};

// ==================== Outside Click By Ref Hook ====================
export const useOutsideClickByRef = (ref, handler) => {
  const handleClickOutside = event => {
    if (ref.current && !ref.current.contains(event.target)) {
      handler();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, handler]);
};

// ==================== Scroll Outside Hook ====================
export function useScrollOutside(parentRef, selfRef, onClose) {
  useEffect(() => {
    const handleScroll = event => {
      if (!selfRef.current || !parentRef.current) return;

      const isInsideSelf = selfRef.current.contains(event.target);

      // Close only if scroll is from parent but not inside self
      if (!isInsideSelf) {
        onClose();
      }
    };

    const parentNode = parentRef.current;
    if (parentNode) {
      parentNode.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      if (parentNode) {
        parentNode.removeEventListener('scroll', handleScroll, true);
      }
    };
  }, [parentRef, selfRef, onClose]);
}

// ==================== Debounce Hook ====================
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ==================== Connection Status Hook ====================
export const useConnectionStatus = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    const updateOnlineStatus = () => {
      dispatch(setConnectionStatus(navigator.onLine));
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, [dispatch]);
};

// ==================== Kit States Hook ====================
export const useKitStates = () => {
  const location = useLocation();
  const isFromShareable = location?.pathname?.includes('shareable-media-kit') && !location?.pathname?.includes('kit-verified')
  const userInfo = useSelector(state => state?.currentUser?.currentUser);
  const currentUserSlice = useSelector(state => state?.currentUser);

  const mkData = useSelector(state => {
    return isFromShareable ? state?.currentUser?.mediaKitLive : state?.currentUser?.mediaKit
  });

  const isSampleKit = mkData?.mediaKit?.isSampleKit;
  const isSavedPassword = mkData?.mediaKit?.isSavedPassword;
  const previewThemeColors = currentUserSlice?.previewThemeColors;
  const usePreviewThemeColors = Boolean(isSampleKit && !isSavedPassword && previewThemeColors);

  const noirTheme =
    (usePreviewThemeColors ? previewThemeColors?.noirThemeColor_chr : null) ??
    mkData?.mediaKit?.noirThemeColor_chr;
  const arcTheme =
    (usePreviewThemeColors ? previewThemeColors?.arcThemeColor_chr : null) ??
    mkData?.mediaKit?.arcThemeColor_chr;
  const pureTheme =
    (usePreviewThemeColors ? previewThemeColors?.pureThemeColor_chr : null) ??
    mkData?.mediaKit?.pureThemeColor_chr;
  const edgeTheme =
    (usePreviewThemeColors ? previewThemeColors?.edgeThemeColor_chr : null) ??
    mkData?.mediaKit?.edgeThemeColor_chr;

  const previewThemeFonts = currentUserSlice?.previewThemeFonts;
  const usePreviewThemeFonts = Boolean(isSampleKit && !isSavedPassword && previewThemeFonts);

  const noirFont =
    (usePreviewThemeFonts ? previewThemeFonts?.noirThemeFont_chr : null) ??
    mkData?.mediaKit?.noirThemeFont_chr;
  const arcFont =
    (usePreviewThemeFonts ? previewThemeFonts?.arcThemeFont_chr : null) ??
    mkData?.mediaKit?.arcThemeFont_chr;
  const pureFont =
    (usePreviewThemeFonts ? previewThemeFonts?.pureThemeFont_chr : null) ??
    mkData?.mediaKit?.pureThemeFont_chr;
  const edgeFont =
    (usePreviewThemeFonts ? previewThemeFonts?.edgeThemeFont_chr : null) ??
    mkData?.mediaKit?.edgeThemeFont_chr;

  const mediaKit = useSelector(state => state?.mediaKit);

  // USERS PLAN TYPE
  const isUserPaid = userInfo?.planType === 'paid';
  const isUserFree = userInfo?.planType === 'free';

  // KITS MODE TYPE
  const isFreeUserKitInPremiumMode = isUserFree && mkData?.mediaKit?.isPremiumMediaKit;
  const isPaidUserKitInPremiumMode = isUserPaid && mkData?.mediaKit?.isPremiumMediaKit;

  // KITS MODE TYPE LOCAL
  const isKitInEditMode = mediaKit?.isEditMediaKit;

  const isBlured = mediaKit?.isBlurMediaKit;
  const activeComponent = mediaKit?.activeComponent;
  const mediaKitType = mediaKit?.mediaKitType;

  const isNewUser = userInfo?.isNewUser;
  const userId = mkData?.mediaKit?.userId || userInfo?.userId;
  const mediaKitId = mkData?.mediaKit?.id;

  // const isPremiumMediaKit = mkData?.mediaKit?.isPremiumMediaKit ?? false;
  const isPremiumMediaKit = true;

  const instaUserId = mkData?.mediaKit?.instaUserId;
  const tiktokUserId = mkData?.mediaKit?.tiktokUserId;

  const isInstagramAuthenticated = mkData?.mediaKit?.isInstagramAuthenticated ?? false;
  const isTiktokAuthenticated = mkData?.mediaKit?.isTiktokAuthenticated ?? false;
  const signInPlatform =
    instaUserId && tiktokUserId
      ? 'both'
      : instaUserId
        ? 'instagram'
        : tiktokUserId
          ? 'tiktok'
          : null;

  const isMediaKitLive = currentUserSlice?.mediaKit?.mediaKit?.isMediaKitLive;
  const isMediaKitEdited = Boolean(mkData?.mediaKit?.isMediaKitEdited_ysn);

  const mail = mkData?.mediaKit?.email;
  const displayEmail = mkData?.mediaKit?.displayEmail;
  const fullName = mkData?.mediaKit?.fullName;

  const isEmailVerified = currentUserSlice?.currentUser?.isEmailVerified || false;

  const isInstaHandlePersonal = mkData?.mediaKit?.isInstaHandlePersonal;

  const isBannersDisplay = isUserFree && isSampleKit && !isKitInEditMode;

  // IS MANDATORY FIELDS
  const isFavFactsMandatory = mediaKit?.mandatorySections?.favFacts?.isMandatory || false;
  const isCTAMandatory = mediaKit?.mandatorySections?.cta?.isMandatory || false;
  const isBrandPartnersMandatory = mediaKit?.mandatorySections?.brandPartners?.isMandatory || false;
  const isTopVideosMandatory = mediaKit?.mandatorySections?.topVideos?.isMandatory || false;
  const isContentPost1Mandatory = mediaKit?.mandatorySections?.contentPost1?.isMandatory || false;
  const isContentPost2Mandatory = mediaKit?.mandatorySections?.contentPost2?.isMandatory || false;
  const isTopPostsMandatory = mediaKit?.mandatoryFields || [];

  const isMandatory =
    isFavFactsMandatory ||
      // isCTAMandatory ||
      isBrandPartnersMandatory ||
      isContentPost1Mandatory ||
      isContentPost2Mandatory ||
      isTopVideosMandatory ||
      isTopPostsMandatory?.length > 0
      ? true
      : false;

  const mandatoryFields = {
    isFavFactsMandatory,
    isCTAMandatory,
    isBrandPartnersMandatory,
    isTopVideosMandatory,
    isTopPostsMandatory,
    isContentPost1Mandatory,
    isContentPost2Mandatory
  };

  // INSTA AUTH FIELDS
  const authInstaFields = mediaKit?.authenticatedFields;

  // CURRENT THEME
  const currentTheme = currentUserSlice?.currentTheme;

  const allSocialChannels = mkData?.bottomSocialChannels?.length > 0
    ? mkData?.bottomSocialChannels
    : mkData?.topSocialChannels;

  const tiktok = allSocialChannels?.filter(channel => channel?.name?.toLowerCase() === 'tiktok');
  const youtube = allSocialChannels?.filter(channel => channel?.name?.toLowerCase() === 'youtube');
  const pinterest = allSocialChannels?.filter(channel => channel?.name?.toLowerCase() === 'pinterest');
  const pinterestUserName = pinterest && (pinterest[0]?.userName || '');
  const youtubeUserName = youtube && (youtube[0]?.userName || '');
  const tiktokUserName = tiktok && (tiktok[0]?.userName || '');
  const instaUserName = mkData?.mediaKit?.userName;
  const isTiktokAdded = allSocialChannels?.some(
    ch => ch?.name?.toLowerCase() === 'tiktok'
  );

  return {
    isFreeUserKitInPremiumMode,
    isPaidUserKitInPremiumMode,
    isUserFree,
    isUserPaid,
    isKitInEditMode,
    mediaKitType,
    isBlured,
    activeComponent,
    isNewUser,
    mediaKitId,
    userId,
    isSampleKit,
    isSavedPassword,
    isPremiumMediaKit,
    isBannersDisplay,
    mkData,
    mediaKitStates: mediaKit?.mediaKitStates,
    instaUserId,
    tiktokUserId,
    isTiktokAuthenticated,
    isInstagramAuthenticated,
    signInPlatform,
    isMediaKitLive,
    isMediaKitEdited,
    mail,
    displayEmail,
    isInstaHandlePersonal,
    isMandatory,
    mandatoryFields,
    authInstaFields,
    currentTheme,
    instaUserName,
    tiktokUserName,
    isTiktokAdded,
    youtubeUserName,
    pinterestUserName,
    isEmailVerified,
    noirTheme,
    arcTheme,
    pureTheme,
    edgeTheme,
    noirFont,
    arcFont,
    pureFont,
    edgeFont,
    fullName
  };
};

// ==================== Theme Font Classes Hook ====================
const themeFontKeyMap = {
  standard: 'pureFont',
  natural: 'noirFont',
  classic: 'arcFont',
  dark: 'edgeFont'
};

export const useThemeFontClasses = theme => {
  const kitStates = useKitStates();
  const normalizedTheme = theme?.toLowerCase() || 'standard';
  const savedGroupKey = kitStates?.[themeFontKeyMap[normalizedTheme]];
  const groupKey = savedGroupKey?.toLowerCase() || normalizedTheme;

  return FONT_GROUPS[groupKey] || FONT_GROUPS[normalizedTheme] || FONT_GROUPS.standard;
};

// ==================== Rotating Text Hook ====================
const ROTATING_POSTS_MESSAGES = [
  'Finding your top content',
  'Sorting through videos',
  'Retrieving your data',
  'Loading images and videos',
];

export const useRotatingText = (messages = ROTATING_POSTS_MESSAGES, intervalMs = 2000) => {
  const [index, setIndex] = useState(0);
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const id = setInterval(() => setIndex(i => (i + 1) % messages.length), intervalMs);
    return () => clearInterval(id);
  }, [messages.length, intervalMs]);

  useEffect(() => {
    const id = setInterval(() => setDotCount(d => d >= 3 ? 1 : d + 1), Math.round(intervalMs / 3));
    return () => clearInterval(id);
  }, [intervalMs]);

  return `${messages[index]}${'.'.repeat(dotCount)}`;
};

// ==================== Social Posts Cache Hook ====================
const SOCIAL_POSTS_CACHE_TTL_MS = 60 * 60 * 1000;
const STORAGE_KEY_PREFIX = 'ilola_spc_v2_';

const readStorageEntry = (cacheKey) => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_PREFIX + cacheKey);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const writeStorageEntry = (cacheKey, entry) => {
  try {
    sessionStorage.setItem(STORAGE_KEY_PREFIX + cacheKey, JSON.stringify(entry));
  } catch { } // ignore quota errors
};

export const getSocialPostsCacheKey = (channel, username) =>
  `${channel?.toLowerCase() || ''}|${username?.toLowerCase() || ''}`;

const defaultNormalizePost = (postItem, index) => ({
  id: postItem?.id || postItem?.videoId || `post-${index}`,
  thumbnail_url:
    postItem?.thumbnail_url ||
    postItem?.thumbnailUrl ||
    postItem?.coverImageUrl ||
    postItem?.media_url ||
    postItem?.mediaUrl ||
    '',
  media_url: postItem?.media_url || postItem?.mediaUrl || '',
  permalink: postItem?.permalink || postItem?.postUrl || postItem?.shareUrl || '',
  media_type: postItem?.media_type || postItem?.mediaType || '',
  media_product_type: postItem?.media_product_type || postItem?.postMediaType || '',
  like_count: postItem?.like_count ?? postItem?.likes ?? 0,
  comments_count: postItem?.comments_count ?? postItem?.comments ?? 0,
  views: postItem?.views ?? 0,
  favourites: postItem?.favourites ?? 0,
  engagement: postItem?.engagement ?? 0,
  engagementRate: postItem?.engagementRate ?? 0,
  children: postItem?.children || null,
  raw: postItem,
});

const parseSocialPostsResponse = (result) => {
  const isNewFormat = result && !Array.isArray(result) && Array.isArray(result?.data);
  return {
    rawPosts: isNewFormat ? result.data : (Array.isArray(result) ? result : []),
    nextCursor: isNewFormat ? (result.nextCursor || null) : null,
    hasMore: isNewFormat ? Boolean(result.hasMore) : false,
  };
};

export const useSocialPostsCache = () => {
  const dispatch = useDispatch();
  const socialPostsCache = useSelector(state => state?.manageMediaKit?.socialPostsCache || {});
  const [postsList, setPostsList] = useState([]);
  const [socialPostsLoading, setSocialPostsLoading] = useState(false);
  const [postsPlaceholderText, setPostsPlaceholderText] = useState('');
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const currentChannelRef = useRef(null);
  const currentUsernameRef = useRef(null);
  const currentNormalizeFnRef = useRef(defaultNormalizePost);

  const fetchPosts = useCallback(async (channel, username, normalizeFn = defaultNormalizePost) => {
    const cacheKey = getSocialPostsCacheKey(channel, username);
    const cachedEntry = socialPostsCache?.[cacheKey] || readStorageEntry(cacheKey);
    const isCacheFresh =
      cachedEntry?.fetchedAt &&
      Array.isArray(cachedEntry?.posts) &&
      cachedEntry.posts.length > 0 &&
      (Date.now() - cachedEntry.fetchedAt) < SOCIAL_POSTS_CACHE_TTL_MS;

    currentChannelRef.current = channel;
    currentUsernameRef.current = username;
    currentNormalizeFnRef.current = normalizeFn;

    setPostsList([]);
    setPostsPlaceholderText('');
    setNextCursor(null);
    setHasMore(false);

    if (isCacheFresh) {
      const cachedPosts = Array.isArray(cachedEntry?.posts) ? cachedEntry.posts : [];
      const restoredCursor = cachedEntry?.nextCursor || null;
      setPostsList(cachedPosts);
      setNextCursor(restoredCursor);
      setHasMore(restoredCursor ? true : Boolean(cachedEntry?.hasMore));
      if (!cachedPosts.length) setPostsPlaceholderText('No recent posts found. You can paste a link below.');
      setSocialPostsLoading(false);
      return cachedPosts;
    }

    if (!username || !channel) {
      setPostsPlaceholderText('Unable to fetch posts right now. You can paste a link below.');
      return [];
    }

    setSocialPostsLoading(true);
    try {
      const data = await dispatch(getSocialMediaPostsListAPIFn({ username, channel, limit: 200 }));
      if (data?.meta?.requestStatus === 'fulfilled') {
        const { rawPosts, nextCursor: newCursor, hasMore: newHasMore } = parseSocialPostsResponse(data?.payload?.result);
        const normalizedPosts = rawPosts.map(normalizeFn);
        const entry = { posts: normalizedPosts, fetchedAt: Date.now(), nextCursor: newCursor, hasMore: newHasMore };
        setPostsList(normalizedPosts);
        setNextCursor(newCursor);
        setHasMore(newHasMore);
        dispatch(setSocialPostsCacheEntry({ key: cacheKey, ...entry }));
        writeStorageEntry(cacheKey, entry);
        if (!normalizedPosts.length) setPostsPlaceholderText('No recent posts found. You can paste a link below.');
        return normalizedPosts;
      }
      setPostsPlaceholderText('Unable to fetch posts right now. You can paste a link below.');
      return [];
    } catch {
      setPostsPlaceholderText('Unable to fetch posts right now. You can paste a link below.');
      return [];
    } finally {
      setSocialPostsLoading(false);
    }
  }, [dispatch, socialPostsCache]);

  const fetchMorePosts = useCallback(async () => {
    const channel = currentChannelRef.current;
    const username = currentUsernameRef.current;
    const normalizeFn = currentNormalizeFnRef.current;

    if (!hasMore || !nextCursor || isFetchingMore || !channel || !username) return;

    setIsFetchingMore(true);
    try {
      const data = await dispatch(getSocialMediaPostsListAPIFn({ username, channel, limit: 200, nextCursor }));
      if (data?.meta?.requestStatus === 'fulfilled') {
        const { rawPosts, nextCursor: newCursor, hasMore: newHasMore } = parseSocialPostsResponse(data?.payload?.result);
        const normalizedPosts = rawPosts.map(normalizeFn);
        setPostsList(prev => {
          const combined = [...prev, ...normalizedPosts];
          const cacheKey = getSocialPostsCacheKey(channel, username);
          const entry = { posts: combined, fetchedAt: Date.now(), nextCursor: newCursor, hasMore: newHasMore };
          dispatch(setSocialPostsCacheEntry({ key: cacheKey, ...entry }));
          writeStorageEntry(cacheKey, entry);
          return combined;
        });
        setNextCursor(newCursor);
        setHasMore(newHasMore);
      }
    } catch {
      // silently fail — user already has current posts
    } finally {
      setIsFetchingMore(false);
    }
  }, [dispatch, hasMore, nextCursor, isFetchingMore]);

  const resetPosts = useCallback(() => {
    setPostsList([]);
    setPostsPlaceholderText('');
    setSocialPostsLoading(false);
    setNextCursor(null);
    setHasMore(false);
    setIsFetchingMore(false);
  }, []);

  return {
    postsList, setPostsList,
    socialPostsLoading, setSocialPostsLoading,
    postsPlaceholderText, setPostsPlaceholderText,
    fetchPosts, fetchMorePosts, resetPosts,
    hasMore, isFetchingMore,
  };
};

const mapMkFavouriteContentsToDisplayList = items => {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => ({
    id: index + 1,
    socialChannelName: item?.socialChannelName,
    videoUrl: item?.videoUrl,
    thumbnailUrl: item?.thumbnailUrl,
    lastUpdatedDateTime_dtm: item?.lastUpdatedDateTime_dtm,
    lastUpdatedByUserID_chr: item?.lastUpdatedByUserID_chr,
    isDeleted_ysn: item?.isDeleted_ysn,
    row_Version_Binary_bin: item?.row_Version_Binary_bin,
    videoId: item?.id
  }));
};

export const useTopVideosDisplayList = (options = {}) => {
  const { respectLocalEdits = false } = options;
  const { mkData } = useKitStates();
  const favouriteContents = mkData?.favouriteContents;

  const [topVideosList, setTopVideosList] = useState([]);
  const localDirtyRef = useRef(false);

  const setTopVideosListTracked = useCallback(
    update => {
      if (respectLocalEdits) {
        localDirtyRef.current = true;
      }
      setTopVideosList(update);
    },
    [respectLocalEdits]
  );

  useEffect(() => {
    if (respectLocalEdits && localDirtyRef.current) return;
    setTopVideosList(mapMkFavouriteContentsToDisplayList(favouriteContents ?? []));
  }, [favouriteContents, respectLocalEdits]);

  return [topVideosList, setTopVideosListTracked];
};
